var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import globalStore from '../store';
import '../styles/widgets/protractorManipulative.scss';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { selectTool, bringManipulativesToFront } from '../helper/helper';
import { snapToBackgroundGrid, snapToTriangleGrid } from '../helper/snapHelper';
import { GRID_SIZE } from '../constants/gridConstants';
import { RotateHandleSVG, ResizeHandleSVG } from './protractor/ProtractorHandles';
import { t } from 'i18next';
import ButtonComponent from '../components/ButtonComponent';
import { playAudio } from '../helper/audioHelper';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
// --- PROTRACTOR IMAGE ---
var PROTRACTOR_IMG = './assets/images/manipulative/protractor/protractor.svg';
var ProtractorManipulative = forwardRef(function (props, ref) {
    var _a, _b, _c;
    var _d, _f, _g;
    var id = props.id, center = props.center, dimensions = props.dimensions, _h = props.scale, scale = _h === void 0 ? 0.6 : _h, _j = props.rotation, rotation = _j === void 0 ? 0 : _j, _k = props.armAngle, armAngle = _k === void 0 ? -120 : _k, _l = props.isGroup, isGroup = _l === void 0 ? false : _l, _m = props.lock, _o = _m === void 0 ? {} : _m, positionLock = _o.positioning;
    // Base dimensions of your SVG file
    var BASE_WIDTH = 562;
    var BASE_HEIGHT = 562;
    var FIXED_HANDLE_DISTANCE = 0;
    var BASE_HANDLE_SIZE = 337;
    var MIN_HANDLE_SIZE = 100;
    var HANDLE_OFFSET = 187;
    // --- GLOBAL STORES ---
    var _p = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _p.zoomLevel, playerBgPattern = _p.playerBgPattern;
    var btnConfig = globalStore.useStoreState(function (state) { return state.jsonDataStore; }).btnConfig;
    var _q = useContext(DataContext), selectedTool = _q.selectedTool, setSelectedTool = _q.setSelectedTool, setManipulative = _q.setManipulative, setSelectedManipulativeId = _q.setSelectedManipulativeId, panningEnable = _q.panningEnable, contextMenu = _q.contextMenu, setContextMenu = _q.setContextMenu;
    var duplicateLabel = useDuplicateAnnounce(id, t('text.protractor'));
    var creationLabel = useCreationAnnounce(id, t('text.protractor'));
    // --- LOCAL STATE ---
    var _r = useState(center), protractorPosition = _r[0], setProtractorPosition = _r[1];
    var _s = useState(rotation), protractorRotation = _s[0], setProtractorRotation = _s[1];
    var _t = useState(scale), protractorScale = _t[0], setProtractorScale = _t[1];
    var _u = useState(dimensions), protractorSize = _u[0], setProtractorSize = _u[1];
    var _v = useState(false), isFocused = _v[0], setIsFocused = _v[1];
    // Transient CSS-only bring-to-front boost while actively dragging (any of
    // move/rotate/resize/angle) — set in attachListeners()/handleGlobalUp, the
    // shared entry/exit points for all four interaction handlers.
    var _w = useState(false), isDragging = _w[0], setIsDragging = _w[1];
    var _x = useState(armAngle), armRotation = _x[0], setArmRotation = _x[1];
    var _y = useState('default'), cursorStyle = _y[0], setCursorStyle = _y[1];
    var _z = useState(''), announceText = _z[0], setAnnounceText = _z[1];
    var _0 = useState(scale), ariaScale = _0[0], setAriaScale = _0[1];
    var _1 = useState(rotation), ariaRotation = _1[0], setAriaRotation = _1[1];
    var _2 = useState(armAngle), ariaArmAngle = _2[0], setAriaArmAngle = _2[1];
    var announce = function (msg) {
        setAnnounceText(msg);
    };
    // Sync local state when props change externally (e.g., undo/redo)
    useEffect(function () {
        setArmRotation(armAngle);
    }, [armAngle]);
    useEffect(function () {
        setProtractorRotation(rotation);
    }, [rotation]);
    useEffect(function () {
        setProtractorScale(scale);
    }, [scale]);
    useEffect(function () {
        setProtractorPosition(center);
    }, [center]);
    // --- REFS (For Logic) ---
    var interactionMode = useRef('none');
    var isInDragZoneRef = useRef(false);
    // VoiceOver double-tap detection
    var lastFocusWasScreenReader = useRef(false);
    var isTouchPrimary = typeof window !== 'undefined' && !!((_d = window.matchMedia) === null || _d === void 0 ? void 0 : _d.call(window, '(pointer: coarse)').matches);
    // Arrow-key movement tracking (debounced save)
    var controllerBtnRef = useRef(null);
    var menuOpenedByUs = useRef(false);
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    var currentPosRef = useRef(center);
    var currentRotRef = useRef(rotation);
    var currentScaleRef = useRef(scale);
    var currentArmRotRef = useRef(armAngle);
    var startValuesRef = useRef({
        pos: { x: 0, y: 0 },
        rotation: 0,
        scale: 1,
        mouseAngleOrDist: 0,
    });
    var startPosRef = useRef({ x: 0, y: 0 });
    var lastTickBucketRef = useRef(Math.floor(armAngle / 5));
    var elementRef = useRef(null);
    var wrapperRef = useRef(null);
    // --- SYNC EFFECTS ---
    useEffect(function () {
        if (interactionMode.current === 'none' && center && !isGroup) {
            setProtractorPosition(center);
            currentPosRef.current = center;
        }
    }, [center, isGroup]);
    useEffect(function () {
        setProtractorRotation(rotation);
        currentRotRef.current = rotation;
    }, [rotation]);
    useEffect(function () {
        setProtractorScale(scale);
        currentScaleRef.current = scale;
    }, [scale]);
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        setIsFocused(nowFocused);
        if (nowFocused && !wasFocused && !isGroup && wrapperRef.current) {
            setTimeout(function () {
                var _a, _b;
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = controllerBtnRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [selectedTool, id, isGroup]);
    // Recalculate dimensions for rendering
    useEffect(function () {
        var newWidth = BASE_WIDTH * protractorScale;
        var newHeight = BASE_HEIGHT * protractorScale;
        setProtractorSize({ width: newWidth, height: newHeight });
    }, [protractorScale]);
    // --- BOTTOM PANEL EVENTS (Scale Buttons) ---
    useEffect(function () {
        var onToolClick = function (data) {
            if (!isFocused)
                return;
            var curScale = currentScaleRef.current;
            var newScale = curScale;
            if (data.id === 'scaleMinus')
                newScale = Math.max(0.5, Math.round((curScale - 0.1) * 10) / 10);
            else if (data.id === 'scalePlus')
                newScale = Math.min(5, Math.round((curScale + 0.1) * 10) / 10);
            if (newScale !== curScale) {
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'scale',
                    old: JSON.stringify(curScale),
                    new: JSON.stringify(newScale),
                });
                setProtractorScale(newScale);
                currentScaleRef.current = newScale;
                setAriaScale(newScale);
                updateGlobalState({ scale: newScale });
                var scaleMsg = t('text.manipulativeScaled', {
                    name: t('text.protractor'),
                    direction: data.id === 'scalePlus' ? t('text.up') : t('text.down'),
                    value: newScale.toFixed(1),
                });
                announce(scaleMsg);
            }
            // Rotate protractor body
            var ROTATE_STEP = 1;
            if (data.id === 'rotateClockwise' || data.id === 'rotateAnticlockwise') {
                var dir = data.id === 'rotateClockwise' ? ROTATE_STEP : -ROTATE_STEP;
                var oldRot = currentRotRef.current;
                var newRot = oldRot + dir;
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'rotation',
                    old: JSON.stringify(oldRot),
                    new: JSON.stringify(newRot),
                });
                currentRotRef.current = newRot;
                setProtractorRotation(newRot);
                setAriaRotation(newRot);
                updateGlobalState({ rotation: newRot });
                var baseline = ((newRot % 360) + 360) % 360;
                var rotMsg = t('text.protractorRotated', {
                    direction: data.id === 'rotateClockwise' ? t('text.clockwise') : t('text.anticlockwise'),
                    degrees: ROTATE_STEP,
                    baseline: Math.round(baseline),
                });
                announce(rotMsg);
            }
            // Adjust arm angle
            var ANGLE_STEP = 1;
            if (data.id === 'angleClockwise' || data.id === 'angleAnticlockwise') {
                var dir = data.id === 'angleClockwise' ? ANGLE_STEP : -ANGLE_STEP;
                var oldAngle = currentArmRotRef.current;
                var newAngle = oldAngle + dir;
                // Wrap around like drag behavior
                if (newAngle > 0)
                    newAngle = newAngle - 360;
                if (newAngle < -360)
                    newAngle = newAngle + 360;
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'armAngle',
                    old: JSON.stringify(oldAngle),
                    new: JSON.stringify(newAngle),
                });
                currentArmRotRef.current = newAngle;
                setArmRotation(newAngle);
                setAriaArmAngle(newAngle);
                updateGlobalState({ armAngle: newAngle });
                var newBucket = Math.floor(newAngle / 5);
                if (newBucket !== lastTickBucketRef.current) {
                    lastTickBucketRef.current = newBucket;
                    playAudio('angleTick');
                }
                var measuredAngle = (180 + newAngle + 360) % 360;
                var baseline = ((currentRotRef.current % 360) + 360) % 360;
                var angleMsg = t('text.protractorAngleChanged', {
                    measuredAngle: Math.round(measuredAngle),
                    baseline: Math.round(baseline),
                });
                announce(angleMsg);
            }
        };
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
        };
    }, [isFocused, id]);
    useEffect(function () {
        if (!contextMenu && menuOpenedByUs.current) {
            menuOpenedByUs.current = false;
            setTimeout(function () { var _a; return (_a = controllerBtnRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 50);
        }
    }, [contextMenu]);
    // Helper: Get Element Center
    var getCenter = function (el) {
        var rect = el.getBoundingClientRect();
        return {
            centerX: rect.left + rect.width / 2,
            centerY: rect.top + rect.height / 2, // Bottom center
        };
    };
    var updateGlobalState = function (newData) {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), newData) }) : item;
            });
        });
    };
    var handleSelection = function (e) {
        // Select. The CSS-only bring-to-front boost (see the wrapper style below)
        // only applies while actively dragging (isDragging), not on selection
        // alone — the manipulative array is only permanently reordered on drop,
        // in handleGlobalUp, so a plain click never touches array order or zIndex.
        // Route through `selectTool` so Shift/Ctrl/Cmd+Click merges/toggles into
        // the existing selection. Use the functional setter form so we read the
        // latest `selectedTool` from React (closure value can be stale across
        // rapid clicks). When no mouse event is in scope (keyboard, VoiceOver,
        // ContextMenu key), fall back to single-select on the id.
        if (e) {
            setSelectedTool(function (prev) { return selectTool(e, id, prev, false); });
            setSelectedManipulativeId(id);
            return;
        }
        if (!selectedTool.includes(id)) {
            setSelectedTool([id]);
            setSelectedManipulativeId(id);
        }
    };
    var dispatchBottomPanel = function (actionId) {
        customEventHandler.dispatchEvent('bottomPanel', { id: actionId, panel: 'bottomPanel' });
    };
    var onMouseMoveContainer = function (_e) {
        if (interactionMode.current !== 'none' || positionLock || !elementRef.current)
            return;
        if (!isInDragZoneRef.current) {
            isInDragZoneRef.current = true;
            setCursorStyle('move');
        }
    };
    var onMouseLeaveContainer = function () {
        if (interactionMode.current !== 'none')
            return;
        isInDragZoneRef.current = false;
        setCursorStyle('default');
    };
    // ==================================================
    // 1. MOUSE DOWN HANDLERS
    // ==================================================
    var _3 = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _3.startMultiDrag, moveMultiDrag = _3.moveMultiDrag, endMultiDrag = _3.endMultiDrag;
    var onMouseDownMove = function (e) {
        // e.preventDefault();
        // e.stopPropagation();
        if (e.target.closest('.controlIcon'))
            return;
        if (positionLock || panningEnable)
            return;
        handleSelection(e);
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        interactionMode.current = 'move';
        startPosRef.current = { x: clientX, y: clientY };
        startValuesRef.current.pos = __assign({}, protractorPosition);
        attachListeners();
        startMultiDrag();
    };
    var onMouseDownRotate = function (e) {
        e.stopPropagation();
        e.preventDefault();
        if (positionLock || panningEnable)
            return;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (elementRef.current) {
            var _a = getCenter(elementRef.current), centerX = _a.centerX, centerY = _a.centerY;
            var angleRad = Math.atan2(clientY - centerY, clientX - centerX);
            var angleDeg = angleRad * (180 / Math.PI);
            interactionMode.current = 'rotate';
            startValuesRef.current.rotation = protractorRotation;
            startValuesRef.current.mouseAngleOrDist = angleDeg;
            attachListeners();
        }
    };
    var onMouseDownResize = function (e) {
        e.stopPropagation();
        e.preventDefault();
        if (positionLock || panningEnable)
            return;
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (elementRef.current) {
            var _a = getCenter(elementRef.current), centerX = _a.centerX, centerY = _a.centerY;
            var dx = clientX - centerX;
            var dy = clientY - centerY;
            var dist = Math.sqrt(dx * dx + dy * dy);
            interactionMode.current = 'resize';
            startValuesRef.current.mouseAngleOrDist = dist;
            startValuesRef.current.scale = protractorScale;
            attachListeners();
        }
    };
    var onMouseDownAngle = function (e) {
        e.stopPropagation();
        e.preventDefault();
        if (positionLock || panningEnable)
            return;
        if (elementRef.current) {
            interactionMode.current = 'angle';
            startValuesRef.current.rotation = armRotation;
            lastTickBucketRef.current = Math.floor(armRotation / 5);
            attachListeners();
        }
    };
    // ==================================================
    // GLOBAL EVENT LISTENERS
    // ==================================================
    var handleGlobalMove = function (e) {
        if (interactionMode.current === 'none')
            return;
        if ('touches' in e)
            e.preventDefault();
        var clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        if (interactionMode.current === 'move') {
            var dx = (clientX - startPosRef.current.x) / zoomLevel;
            var dy = (clientY - startPosRef.current.y) / zoomLevel;
            var newX = startValuesRef.current.pos.x + dx;
            var newY = startValuesRef.current.pos.y + dy;
            var newPos = { x: newX, y: newY };
            setProtractorPosition(newPos);
            currentPosRef.current = newPos;
            // updateGlobalState({ center: { x: newX, y: newY } });
            moveMultiDrag(dx, dy);
        }
        else if (interactionMode.current === 'rotate' && elementRef.current) {
            var _a = getCenter(elementRef.current), centerX = _a.centerX, centerY = _a.centerY;
            var angleRad = Math.atan2(clientY - centerY, clientX - centerX);
            var currentAngleDeg = angleRad * (180 / Math.PI);
            var angleDiff = currentAngleDeg - startValuesRef.current.mouseAngleOrDist;
            var newRot = startValuesRef.current.rotation + angleDiff;
            setProtractorRotation(newRot);
            currentRotRef.current = newRot;
        }
        else if (interactionMode.current === 'resize' && elementRef.current) {
            var _b = getCenter(elementRef.current), centerX = _b.centerX, centerY = _b.centerY;
            var dx = clientX - centerX;
            var dy = clientY - centerY;
            var currentDist = Math.sqrt(dx * dx + dy * dy);
            var ratio = currentDist / startValuesRef.current.mouseAngleOrDist;
            var newScale = Math.min(5, Math.max(0.5, startValuesRef.current.scale * ratio));
            setProtractorScale(newScale);
            currentScaleRef.current = newScale;
        }
        else if (interactionMode.current === 'angle' && elementRef.current) {
            var _c = getCenter(elementRef.current), centerX = _c.centerX, centerY = _c.centerY;
            var angleRad = Math.atan2(clientY - centerY, clientX - centerX);
            var mouseGlobleDeg = angleRad * (180 / Math.PI);
            var localAngle = mouseGlobleDeg - protractorRotation;
            if (localAngle > 0)
                localAngle -= 360;
            var currentBucket = Math.floor(localAngle / 5);
            if (currentBucket !== lastTickBucketRef.current) {
                lastTickBucketRef.current = currentBucket;
                playAudio('angleTick', { overlap: true, maxOverlap: 7 });
            }
            setArmRotation(localAngle);
            currentArmRotRef.current = localAngle;
        }
    };
    var handleGlobalUp = function () {
        var mode = interactionMode.current;
        if (mode === 'none')
            return;
        removeListeners();
        interactionMode.current = 'none';
        setIsDragging(false);
        isInDragZoneRef.current = false;
        setCursorStyle('default');
        var changed = false;
        if (mode === 'move') {
            var finalPos = currentPosRef.current;
            if (playerBgPattern !== 'none') {
                var snapped = playerBgPattern === 'triangle'
                    ? snapToTriangleGrid(finalPos.x, finalPos.y)
                    : snapToBackgroundGrid(finalPos.x, finalPos.y);
                finalPos = snapped;
                setProtractorPosition(snapped);
                currentPosRef.current = snapped;
            }
            var oldPos = startValuesRef.current.pos;
            if (finalPos.x !== oldPos.x || finalPos.y !== oldPos.y) {
                changed = true;
                var entry = {
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'center',
                    old: JSON.stringify(oldPos),
                    new: JSON.stringify(finalPos),
                };
                var registered = registerMultiSelectSaveEntries(id, [entry]);
                if (!registered) {
                    saveState(entry);
                }
                updateGlobalState({ center: finalPos });
            }
            endMultiDrag();
        }
        else if (mode === 'rotate') {
            if (currentRotRef.current !== startValuesRef.current.rotation) {
                changed = true;
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'rotation',
                    old: JSON.stringify(startValuesRef.current.rotation),
                    new: JSON.stringify(currentRotRef.current),
                });
                updateGlobalState({ rotation: currentRotRef.current });
            }
            setAriaRotation(currentRotRef.current);
        }
        else if (mode === 'resize') {
            if (currentScaleRef.current !== startValuesRef.current.scale) {
                changed = true;
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'scale',
                    old: JSON.stringify(startValuesRef.current.scale),
                    new: JSON.stringify(currentScaleRef.current),
                });
            }
            setAriaScale(currentScaleRef.current);
            updateGlobalState({ scale: currentScaleRef.current });
        }
        else if (mode === 'angle') {
            if (currentArmRotRef.current !== startValuesRef.current.rotation) {
                changed = true;
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'armAngle',
                    old: JSON.stringify(startValuesRef.current.rotation),
                    new: JSON.stringify(currentArmRotRef.current),
                });
                updateGlobalState({ armAngle: currentArmRotRef.current });
            }
            setAriaArmAngle(currentArmRotRef.current);
        }
        // Bring to front by moving this protractor to the end of the manipulative
        // array. Only committed here, on drop after an actual change (move/
        // rotate/resize/angle) — a plain click with no movement never reorders
        // the array (CSS zIndex handles the click/drag-in-progress visual boost
        // instead, see the wrapper style above).
        if (changed) {
            setManipulative(function (prev) {
                var beforeIds = prev.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var alreadyAtFront = beforeIds[beforeIds.length - 1] === id;
                if (alreadyAtFront)
                    return prev;
                var reordered = bringManipulativesToFront(prev, [id]);
                saveState({
                    type: id,
                    id: id,
                    propertyName: 'reorder',
                    old: JSON.stringify(beforeIds),
                    new: JSON.stringify(reordered.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; })),
                });
                return reordered;
            });
        }
    };
    var attachListeners = function () {
        setIsDragging(true);
        window.addEventListener('mousemove', handleGlobalMove);
        window.addEventListener('mouseup', handleGlobalUp);
        window.addEventListener('touchmove', handleGlobalMove, { passive: false });
        window.addEventListener('touchend', handleGlobalUp);
    };
    var removeListeners = function () {
        window.removeEventListener('mousemove', handleGlobalMove);
        window.removeEventListener('mouseup', handleGlobalUp);
        window.removeEventListener('touchmove', handleGlobalMove);
        window.removeEventListener('touchend', handleGlobalUp);
    };
    var getSectorGradient = function (color) {
        // Reference arm at 180deg CSS (left = protractor 0°/360° = 270deg conic).
        // Sector fills clockwise from the reference to the arm (inner/clockwise sector).
        var armConic = (((90 + armRotation) % 360) + 360) % 360;
        var span = (((armConic - 270) % 360) + 360) % 360;
        return "conic-gradient(from 270deg, ".concat(color, " 0deg ").concat(span, "deg, transparent ").concat(span, "deg 360deg)");
    };
    useImperativeHandle(ref, function () { return ({}); });
    var dynamicHandleSize = Math.max(MIN_HANDLE_SIZE, BASE_HANDLE_SIZE * protractorScale);
    // Offset to align handle's red dot exactly at arm pivot point (center of protractor).
    // The red dot sits 3.5 viewBox units from the SVG edge (circle radius) in a ~448-unit-tall viewBox.
    // After CSS rotate(90deg), this becomes a horizontal offset that must scale with handle size.
    var handleDotOffset = (dynamicHandleSize * 3.5) / 448;
    // Blue icon center in the SVG, mapped to X offset after CSS rotate(90deg)
    var blueIconOffset = (dynamicHandleSize * 420) / 448 - handleDotOffset;
    // Blue icon (circle + pointer) scales with handle size (~60 viewBox units in 448-tall SVG)
    var anchorSize = (dynamicHandleSize * 60) / 448;
    // Tooltip font size scales with protractor (base 20px at scale 1)
    var tooltipFontSize = 20 * protractorScale;
    return (_jsxs("div", { ref: wrapperRef, 
        // `protractorSelected` carries the selected treatment (see
        // protractorManipulative.scss) instead of an inline style, so
        // `captureMainHolderScreenshot` can drop the class for the duration of a
        // screenshot. As an inline `border: 2px solid #293273` it could not be
        // suppressed: the capture's colour rules deliberately never match
        // `border-color`, because borders are real content on other widgets.
        className: "protractorWrapper".concat(isFocused ? ' protractorSelected' : ''), "data-manipulative-id": id, "data-MFDeselect": "true", onFocus: function (e) {
            var _a, _b;
            try {
                lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
            }
            catch (_c) {
                lastFocusWasScreenReader.current = false;
            }
        }, onBlur: function () {
            lastFocusWasScreenReader.current = false;
        }, style: {
            position: isGroup ? 'relative' : 'absolute',
            top: isGroup ? 0 : protractorPosition.y,
            left: isGroup ? 0 : protractorPosition.x,
            transform: isGroup ? 'none' : 'translate(-50%, -50%)',
            zIndex: isDragging ? 9999 : undefined,
            pointerEvents: 'auto',
        }, children: [_jsxs("div", { ref: elementRef, role: "presentation", className: "protractorContainer ".concat(interactionMode.current !== 'none' ? 'grabbing' : ''), style: {
                    width: protractorSize.width,
                    height: protractorSize.height,
                    transform: "rotate(".concat(protractorRotation, "deg)"),
                    transformOrigin: 'center center',
                    cursor: cursorStyle,
                }, onMouseDown: onMouseDownMove, onTouchStart: onMouseDownMove, onMouseMove: onMouseMoveContainer, onMouseLeave: onMouseLeaveContainer, children: [_jsx("img", { src: PROTRACTOR_IMG, className: "protractorImage", draggable: false, alt: "", style: { pointerEvents: 'none' } }), _jsx(ButtonComponent, { reference: controllerBtnRef, avoidDefaultStyle: true, defaultCSS: "protractorKeyboardController", isParentRelative: true, noAudio: true, tabIndex: isGroup ? -1 : 0, ariaHidden: isGroup ? true : undefined, ariaLabel: (_f = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _f !== void 0 ? _f : "".concat(t('text.protractor'), ", ").concat(t('text.protractorAngleChanged', {
                            measuredAngle: Math.round((180 + armRotation + 360) % 360),
                            baseline: Math.round(((protractorRotation % 360) + 360) % 360),
                        })), clickHandler: function (e) {
                            var _a;
                            if (isGroup || panningEnable)
                                return;
                            var isKeyboardOrVoiceOver = (isTouchPrimary && lastFocusWasScreenReader.current) ||
                                (!isTouchPrimary && e.detail === 0);
                            if (isKeyboardOrVoiceOver) {
                                e.preventDefault();
                                handleSelection();
                                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect) {
                                    menuOpenedByUs.current = true;
                                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                                }
                            }
                        }, onKeyDown: function (e) {
                            var _a, _b;
                            if (isGroup || panningEnable)
                                return;
                            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                                e.preventDefault();
                                e.stopPropagation();
                                if (positionLock)
                                    return;
                                var curPos = (_a = keyMoveCurPosRef.current) !== null && _a !== void 0 ? _a : protractorPosition;
                                if (!keyMoveStartRef.current)
                                    keyMoveStartRef.current = __assign({}, curPos);
                                var newPos = void 0;
                                if (playerBgPattern !== 'none') {
                                    var nudge = GRID_SIZE * 0.6;
                                    var nudgeMap = {
                                        ArrowUp: { x: 0, y: -nudge },
                                        ArrowDown: { x: 0, y: nudge },
                                        ArrowLeft: { x: -nudge, y: 0 },
                                        ArrowRight: { x: nudge, y: 0 },
                                    };
                                    var n = nudgeMap[e.key];
                                    var nudged = { x: curPos.x + n.x, y: curPos.y + n.y };
                                    newPos =
                                        playerBgPattern === 'triangle'
                                            ? snapToTriangleGrid(nudged.x, nudged.y)
                                            : snapToBackgroundGrid(nudged.x, nudged.y);
                                }
                                else {
                                    var step = (e.shiftKey ? 20 : 5) / zoomLevel;
                                    var delta = {
                                        ArrowUp: { x: 0, y: -step },
                                        ArrowDown: { x: 0, y: step },
                                        ArrowLeft: { x: -step, y: 0 },
                                        ArrowRight: { x: step, y: 0 },
                                    };
                                    var d = delta[e.key];
                                    newPos = { x: curPos.x + d.x, y: curPos.y + d.y };
                                }
                                keyMoveCurPosRef.current = newPos;
                                setProtractorPosition(newPos);
                                currentPosRef.current = newPos;
                                if (keyMoveSaveTimer.current)
                                    clearTimeout(keyMoveSaveTimer.current);
                                keyMoveSaveTimer.current = setTimeout(function () {
                                    if (keyMoveStartRef.current && keyMoveCurPosRef.current) {
                                        var oldPos = keyMoveStartRef.current;
                                        var newP = keyMoveCurPosRef.current;
                                        if (newP.x !== oldPos.x || newP.y !== oldPos.y) {
                                            saveState({
                                                type: id,
                                                id: id.split('_')[1],
                                                propertyName: 'center',
                                                old: JSON.stringify(oldPos),
                                                new: JSON.stringify(newP),
                                            });
                                        }
                                        updateGlobalState({ center: newP });
                                        var dx = Math.round(newP.x - oldPos.x);
                                        var dy = Math.round(newP.y - oldPos.y);
                                        var dirs = [];
                                        if (dy < 0)
                                            dirs.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                                        if (dy > 0)
                                            dirs.push("".concat(t('text.down'), " ").concat(dy));
                                        if (dx < 0)
                                            dirs.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                                        if (dx > 0)
                                            dirs.push("".concat(t('text.right'), " ").concat(dx));
                                        var moveMsg = t('text.manipulativeMoved', {
                                            name: t('text.protractor'),
                                            direction: dirs.join(" ".concat(t('text.and'), " ")),
                                            x: Math.round(newP.x),
                                            y: Math.round(newP.y),
                                        });
                                        announce(moveMsg);
                                        keyMoveStartRef.current = null;
                                        keyMoveCurPosRef.current = null;
                                    }
                                }, 500);
                                return;
                            }
                            // ContextMenu / Shift+F10 don't fire onClick — handle here
                            if (e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10')) {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelection();
                                var rect = (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
                                if (rect) {
                                    menuOpenedByUs.current = true;
                                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                                }
                            }
                            // Enter / Space handled by native onClick (detail=0) — no onKeyDown needed
                        } }), !positionLock && !panningEnable && (_jsxs(_Fragment, { children: [_jsx("div", { "aria-hidden": true, className: "angleSector", style: {
                                    background: getSectorGradient('rgba(218, 165, 32, 0.5)'),
                                    width: '100%',
                                    height: '100%',
                                } }), _jsx("div", { "aria-hidden": true, className: "angleSector", style: {
                                    background: getSectorGradient('rgba(0, 168, 255, 0.5)'),
                                    width: '15%',
                                    height: '15%',
                                    zIndex: 15,
                                    pointerEvents: 'none',
                                } }), _jsx("div", { "aria-hidden": true, style: {
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '15%',
                                    height: '15%',
                                    borderRadius: '50%',
                                    border: '4px dashed #c42938',
                                    background: 'transparent',
                                    pointerEvents: 'none',
                                    zIndex: 101,
                                    WebkitMaskImage: getSectorGradient('black'),
                                    maskImage: getSectorGradient('black'),
                                } }), _jsxs("div", { className: "controlArm", style: {
                                    transform: "rotate(180deg)",
                                    zIndex: 100,
                                }, children: [_jsx(RotateHandleSVG, { "aria-hidden": true, className: "controlIcon", style: {
                                            width: "".concat(dynamicHandleSize, "px"),
                                            height: "".concat(dynamicHandleSize, "px"),
                                            marginLeft: "".concat(-handleDotOffset, "px"),
                                        }, onMouseDown: onMouseDownRotate, onTouchStart: onMouseDownRotate }), _jsx(ButtonComponent, { showToolTip: true, title: "text.protractorRotateHandle", direction: "bottom", toolTipCSS: "protractorTooltip", ariaLabel: "text.protractorRotateHandle", role: "slider", ariaValueNow: Math.round(((ariaRotation % 360) + 360) % 360), ariaValueMin: -1, ariaValueMax: 360, avoidDefaultStyle: true, defaultCSS: "protractorTooltipAnchor", isParentRelative: true, noAudio: true, tabIndex: isGroup ? -1 : 0, ariaHidden: isGroup ? true : undefined, onMouseDown: onMouseDownRotate, onTouchStart: onMouseDownRotate, onKeyDown: function (e) {
                                            if (['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown'].includes(e.key)) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSelection();
                                                dispatchBottomPanel(e.key === 'ArrowLeft' || e.key === 'ArrowDown'
                                                    ? 'rotateAnticlockwise'
                                                    : 'rotateClockwise');
                                            }
                                        }, buttonStyle: (_a = {},
                                            _a['--tooltip-counter-rotate'] = "".concat(-(protractorRotation + 180), "deg"),
                                            _a['--handle-offset'] = "".concat(blueIconOffset, "px"),
                                            _a['--anchor-size'] = "".concat(anchorSize, "px"),
                                            _a['--tooltip-font-size'] = "".concat(tooltipFontSize, "px"),
                                            _a) })] }), _jsxs("div", { className: "controlArm", style: {
                                    width: "".concat(FIXED_HANDLE_DISTANCE, "px"),
                                    transform: "rotate(".concat(armRotation, "deg)"),
                                }, children: [_jsx(ResizeHandleSVG, { "aria-hidden": true, className: "controlIcon", style: {
                                            width: "".concat(dynamicHandleSize, "px"),
                                            height: "".concat(dynamicHandleSize, "px"),
                                            marginLeft: "".concat(-handleDotOffset, "px"),
                                        }, onMouseDown: onMouseDownAngle, onTouchStart: onMouseDownAngle }), _jsx(ButtonComponent, { showToolTip: true, title: "text.protractorAngleHandle", direction: "bottom", toolTipCSS: "protractorTooltip", ariaLabel: "text.protractorAngleHandle", role: "slider", ariaValueNow: Math.round((180 + ariaArmAngle + 360) % 360), ariaValueMin: -1, ariaValueMax: 361, avoidDefaultStyle: true, defaultCSS: "protractorTooltipAnchor", isParentRelative: true, noAudio: true, tabIndex: isGroup ? -1 : 0, ariaHidden: isGroup ? true : undefined, onMouseDown: onMouseDownAngle, onTouchStart: onMouseDownAngle, onKeyDown: function (e) {
                                            if (['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown'].includes(e.key)) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleSelection();
                                                dispatchBottomPanel(e.key === 'ArrowLeft' || e.key === 'ArrowDown'
                                                    ? 'angleAnticlockwise'
                                                    : 'angleClockwise');
                                            }
                                        }, buttonStyle: (_b = {},
                                            _b['--tooltip-counter-rotate'] = "".concat(-(protractorRotation + armRotation), "deg"),
                                            _b['--handle-offset'] = "".concat(blueIconOffset, "px"),
                                            _b['--anchor-size'] = "".concat(anchorSize, "px"),
                                            _b['--tooltip-font-size'] = "".concat(tooltipFontSize, "px"),
                                            _b) })] }), _jsx(ButtonComponent, { showToolTip: true, title: "text.protractorResizeHandle", direction: "bottom", toolTipCSS: "protractorTooltip", ariaLabel: "text.protractorResizeHandle", role: "slider", ariaValueNow: Math.round(ariaScale * 10) / 10, ariaValueMin: 0.5, ariaValueMax: 5, avoidDefaultStyle: true, defaultCSS: "protractorResizeBtn", imgSrc: (_g = btnConfig === null || btnConfig === void 0 ? void 0 : btnConfig.resizeProtractor) === null || _g === void 0 ? void 0 : _g.imgSrc, isParentRelative: true, noAudio: true, tabIndex: isGroup ? -1 : 0, ariaHidden: isGroup ? true : undefined, onMouseDown: onMouseDownResize, onTouchStart: onMouseDownResize, onKeyDown: function (e) {
                                    if (['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown'].includes(e.key)) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleSelection();
                                        dispatchBottomPanel(e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? 'scaleMinus' : 'scalePlus');
                                    }
                                }, buttonStyle: (_c = {},
                                    _c['--tooltip-counter-rotate'] = "".concat(-protractorRotation, "deg"),
                                    _c['--tooltip-font-size'] = "".concat(tooltipFontSize, "px"),
                                    _c['--resize-btn-size'] = "".concat(protractorSize.width * 0.065, "px"),
                                    _c['--resize-btn-top'] = "calc(".concat((0.5 - 0.5 * 0.707) * 100, "% - ").concat(protractorSize.width * 0.065, "px)"),
                                    _c['--resize-btn-right'] = "calc(".concat((0.5 - 0.5 * 0.707) * 100, "% - ").concat(protractorSize.width * 0.065, "px)"),
                                    _c) })] }))] }), _jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: announceText })] }));
});
export default ProtractorManipulative;
